import { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, Inbox, Users as UsersIcon, MessageSquare, Activity } from "lucide-react";

const ICONS = {
  candidates: FileText,
  enquiries: Inbox,
  contacts: MessageSquare,
  users: UsersIcon,
};

const LINKS = {
  candidates: "/admin/candidates",
  enquiries: "/admin/enquiries",
  contacts: "/admin/contacts",
  users: "/admin/users",
};

const LABELS = {
  candidates: "Network",
  enquiries: "Enquiries",
  contacts: "Messages",
  users: "Users",
};

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    api.get("/stats")
      .then((r) => active && setData(r.data))
      .catch((e) => active && setError(e?.response?.data?.detail || "Failed to load"));
    return () => { active = false; };
  }, []);

  const counts = data?.counts || {};
  const byDiscipline = data?.by_discipline || {};
  const byAvailability = data?.by_availability || {};

  return (
    <AdminLayout title="Overview" subtitle="Operational signal across the Nexra platform.">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats-grid">
        {["candidates", "enquiries", "contacts", "users"].map((k) => {
          const Icon = ICONS[k];
          return (
            <Link
              key={k}
              to={LINKS[k]}
              className="group border border-border p-5 bg-white hover:border-obsidian transition-colors"
              data-testid={`stat-card-${k}`}
            >
              <div className="flex items-center justify-between mb-6">
                <Icon className="h-5 w-5 text-execution" />
                <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-obsidian transition-colors" />
              </div>
              <div className="font-heading text-4xl font-bold tracking-tighter">{counts[k] ?? "—"}</div>
              <div className="mt-2 text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">{LABELS[k]}</div>
            </Link>
          );
        })}
      </div>

      {/* Network distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        <Panel title="Network by discipline group" linkTo="/admin/candidates" testid="discipline-distribution">
          {Object.keys(byDiscipline).length === 0 ? (
            <Empty>No professionals in the network yet.</Empty>
          ) : (
            <ul className="space-y-3">
              {Object.entries(byDiscipline).map(([group, n]) => {
                const max = Math.max(...Object.values(byDiscipline));
                const pct = max ? (n / max) * 100 : 0;
                return (
                  <li key={group}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-medium">{group}</span>
                      <span className="font-mono text-xs text-muted-foreground">{n}</span>
                    </div>
                    <div className="h-1.5 bg-muted">
                      <div className="h-full bg-execution transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        <Panel title="Network availability" linkTo="/admin/candidates" testid="availability-distribution">
          {Object.keys(byAvailability).length === 0 ? (
            <Empty>No availability data yet.</Empty>
          ) : (
            <ul className="divide-y divide-border">
              {Object.entries(byAvailability).map(([a, n]) => (
                <li key={a} className="py-2.5 flex items-center justify-between">
                  <span className="text-sm truncate">{a}</span>
                  <span className="chip"><Activity className="h-3 w-3 text-execution" />{n}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <Panel title="Recent network applications" linkTo="/admin/candidates" testid="recent-candidates">
          {data?.recent_candidates?.length ? (
            <ul className="divide-y divide-border">
              {data.recent_candidates.map((c) => (
                <li key={c.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{c.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{c.discipline} · {c.email}</div>
                  </div>
                  <div className="text-[11px] font-mono text-muted-foreground whitespace-nowrap">
                    {new Date(c.submitted_at).toLocaleDateString()}
                  </div>
                </li>
              ))}
            </ul>
          ) : <Empty>No applications yet.</Empty>}
        </Panel>

        <Panel title="Recent enquiries" linkTo="/admin/enquiries" testid="recent-enquiries">
          {data?.recent_enquiries?.length ? (
            <ul className="divide-y divide-border">
              {data.recent_enquiries.map((e) => (
                <li key={e.id} className="py-3">
                  <div className="flex items-center justify-between gap-4">
                    <div className="font-medium truncate">{e.organisation}</div>
                    <div className="text-[11px] font-mono text-muted-foreground whitespace-nowrap">
                      {new Date(e.submitted_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{e.name} — {e.description}</div>
                </li>
              ))}
            </ul>
          ) : <Empty>No client enquiries yet.</Empty>}
        </Panel>
      </div>

      {error && <div className="mt-4 text-sm text-destructive">{error}</div>}
    </AdminLayout>
  );
}

function Panel({ title, children, linkTo, testid }) {
  return (
    <div className="border border-border bg-white p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div className="overline">{title}</div>
        {linkTo && (
          <Link to={linkTo} className="text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-obsidian">
            View all →
          </Link>
        )}
      </div>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <div className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border">{children}</div>;
}
